import { ReactNode } from 'react';
import { Flex, Pane, spaces, Text, InitialsInCircle } from '@electricjs/arc';

import { SearchHintOption } from '@/components/SearchHintOption';
import { EMPLOYEE_SEARCH_HINT_VALUE } from '@/components/People/transformOptions';

type EmployeeSelectOptionProps = {
  data: {
    value?: string;
    label: string;
    name: string;
    jobTitle?: string;
    jobTitleSecondaryText?: string;
    email?: string;
  };
  // Rendered right next to the name, e.g. an on-leave indicator icon.
  nameAdornment?: ReactNode;
  // Dims the name/email/job title text to match the rest of a disabled
  // select option - callers that render this via a custom Option component
  // bypass the select's own dimming, which only applies to plain text.
  isDisabled?: boolean;
};

export const EmployeeSelectOption = ({
  data,
  nameAdornment,
  isDisabled,
}: EmployeeSelectOptionProps) => {
  if (data.value === EMPLOYEE_SEARCH_HINT_VALUE) {
    return <SearchHintOption label={data.label} />;
  }

  return (
    <Pane py={spaces.space_3xs_rem} flexWrap="nowrap">
      <Pane mr={spaces.space_m_rem} width="auto">
        <InitialsInCircle
          text={data.label}
          textIntent={isDisabled ? 'disabled' : undefined}
        />
      </Pane>
      <Pane stack>
        <Flex alignItems="center" gap={spaces.space_2xs}>
          <Text
            variant="label-large"
            intent={isDisabled ? 'disabled' : undefined}>
            {data.name}
          </Text>
          {nameAdornment}
        </Flex>
        {data.email && (
          <Text variant="body-2" intent={isDisabled ? 'disabled' : 'primary'}>
            {data.email}
          </Text>
        )}
        {data.jobTitle && (
          <Text variant="body-2" intent={isDisabled ? 'disabled' : 'secondary'}>
            {data.jobTitle}
          </Text>
        )}
        {data.jobTitleSecondaryText && (
          <Text variant="legal" intent={isDisabled ? 'disabled' : 'secondary'}>
            {data.jobTitleSecondaryText}
          </Text>
        )}
      </Pane>
    </Pane>
  );
};
